import {Injectable, OnDestroy} from '@angular/core';
import {ViewFault} from '@docma-mm-core/fault/fault';
import {SelectionService} from '@mydocma/component-library/src/lib/selection';
import {MarkerService, PinService} from '@mydocma/tile-viewer';
import {debounceTime, distinctUntilChanged, filter, first, map, switchMap, tap, timeout} from 'rxjs/operators';
import {MmFaultsViewService} from '../../../mm-faults-view/services/mm-faults-view.service';
import {MmPlansManagerService} from '../../services/mm-plans-manager.service';
import {MmPlansFilterService} from '../../services/mm-plans-filter.service';
import {BehaviorSubject, firstValueFrom, Observable, Subscription} from 'rxjs';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {MmFaultsSplitService} from '../../../mm-faults-view/services/mm-faults-split.service';
import {MmPlanEditService} from './mm-plan-edit.service';
import {PlanEditModeEnum} from './plan-edit-mode.enum';
import {AppContainerService} from '@mydocma/app-container';
import {DeviceService} from '@mydocma/core/src/lib/device';
import {Pin} from '@mydocma/tile-viewer/lib/pin/pin';
import {MmPinPopupService} from '../../mm-plan-popup/mm-pin-popup.service';
import {Logger} from '@mydocma/common/src/lib/logger';


const PIN_LOAD_TIMEOUT_MS = 5000;

@UntilDestroy()
@Injectable()
export class MmPlanPinSelectionService implements OnDestroy {

  public planPinService: PinService;
  private markerSrvice: MarkerService;
  private isPlanSetBySelection = false;
  private subscription: Subscription;

  onSelectedPinsChangedSubject = new BehaviorSubject<ViewFault[]>(null);
  onSelectedPinsChanged$ = this.onSelectedPinsChangedSubject.asObservable();


  protected takeUntilPlanSelected  = () => <T>(source: Observable<T>) => {
    return source.pipe(
      switchMap(v => this.plansManagerService.selectedPlan$.pipe(filter(Boolean), map(m => v)))
    );
  }

  protected takeUntilPlanViewExists = () => <T>(source: Observable<T>) => {
    return source.pipe(
        switchMap(v => this.faultsSplitService.rightViewVisible$.pipe(filter(Boolean), map(m => v)))
    );
  }

  constructor(private readonly selectionFaultService: SelectionService<ViewFault>,
              private readonly faultsViewService: MmFaultsViewService,
              private readonly plansManagerService: MmPlansManagerService,
              private readonly plansFilterService: MmPlansFilterService,
              private readonly faultsSplitService: MmFaultsSplitService,
              private readonly planEditService: MmPlanEditService,
              private readonly appContainerService: AppContainerService,
              private readonly deviceService: DeviceService,
              private readonly pinPopupService: MmPinPopupService,
              private readonly logger: Logger) {

  }

  ngOnDestroy(): void {
  }

  unregisterPlanPinService() {
    this.subscription?.unsubscribe();
    delete this.planPinService;
    delete this.markerSrvice;
    this.subscription = new Subscription();
  }

  registerPlanPinService(pinService: PinService, markerService: MarkerService): void {
    this.subscription?.unsubscribe();
    this.planPinService = pinService;
    this.markerSrvice = markerService;
    this.subscription = new Subscription();

    this.subscription.add(this.planPinService.onPinSelectionChanged.pipe(
      filter(Boolean),
      this.takeUntilPlanSelected(),
      this.takeUntilPlanViewExists(),
      filter(p => this.planEditService.planEditMode === PlanEditModeEnum.None),
      untilDestroyed(this)
    ).subscribe({
      next: async ({changes, selection}) => {
        const selectedPlan = await firstValueFrom(this.plansManagerService.selectedPlan$);

        if (selection.length !== this.planPinService.selectedPins.length) {
          selection = this.planPinService.selectedPins;
        }
        // console.warn('onPinSelectionChanged');
        // console.warn(changes);

        // if(changes.deselected.length === 1 && selection.length === 0 && !await firstValueFrom(this.appRightPaneService.isOpen) && !this.appContainerService.isMobileView) {
        //   this.faultsViewService.openRightPane(changes.deselected[0].data);
        // }
        changes.deselected.map(pin => selectedPlan.pins?.find(p => p.fault.identifier === pin.id || p.fault.uid === pin.id)?.fault)
                          .forEach(fault => {
                            this.faultsViewService.setCurrentFault(null);
                            this.selectionFaultService.deselectItem(fault);
                          });

        if (selection.length > 1) {
          this.selectionFaultService.startSelectionMode();
          selection.map(pin => selectedPlan.pins?.find(p => p.fault.identifier === pin.id || p.fault.uid === pin.id)?.fault)
                   .forEach(fault => {
                     if (fault) {
                       this.selectionFaultService.selectItem(fault);
                     }
                   });

          this.onSelectedPinsChangedSubject.next(await firstValueFrom(this.selectionFaultService.selectedItems));

        } else if ( selection.length >= 1) {
          const fault = selectedPlan.pins?.find(p => p.fault.identifier === selection[0].id || p.fault.uid === selection[0].id)?.fault;
          const currentFault = await firstValueFrom(this.faultsViewService.currentFault);
          if (!(this.appContainerService.isMobileView && currentFault?.unsavedNew) && fault) {
            this.faultsViewService.setCurrentFault(fault);
            this.onSelectedPinsChangedSubject.next([fault]);
          }
          if (fault?.data && this.deviceService.isDesktop()) {
            this.faultsViewService.openRightPane(fault);
          }
        }
        else {
          if (!this.appContainerService.isMobileView && !!(await firstValueFrom(this.selectionFaultService.selectionMode))) {
            this.faultsViewService.closeRightPane();
          }
        }
      },
      error: error => this.logger.error(error, '', 'MmPlanPinSelectionService')
    }));


    this.subscription.add(this.selectionFaultService.itemSelectionChange.pipe(
      distinctUntilChanged(),
      untilDestroyed(this),
      this.takeUntilPlanViewExists(),
      filter(p => this.planEditService.planEditMode === PlanEditModeEnum.None),
    ).subscribe({
      next: async ({item, selected}) => {
        const pin = this.findPinForFault(item);
        if (!pin) {
          return;
        }

        const selectedPinIds = this.getSelectedPinIds();
        const isPinSelected = selectedPinIds.has(pin.id);
        if (selected === isPinSelected) {
          return;
        }

        if (selected) {
          this.planPinService.selectPin(pin, false);
          await this.selectFault(pin);
          if (await this.shouldOpenPinPopup()) {
            this.pinPopupService.openPopup(pin.id);
          }
        } else {
          this.planPinService.deselectPin(pin, false);
          this.pinPopupService.closePopup(pin.id);
        }
      },
      error: (error) => this.logger.error(error, '', 'MmPlanPinSelectionService')
    }));

    this.subscription.add(this.selectionFaultService.selectionMode.pipe(
      untilDestroyed(this),
      this.takeUntilPlanViewExists(),
      filter(p => this.planEditService.planEditMode === PlanEditModeEnum.None),
    ).subscribe({
        next: (mode) => {
          this.planPinService.pinSelectionOptions = {keepExistingSelection: mode};
          if (mode === false) {
            this.isPlanSetBySelection = mode;
          }
        },
        error: (error) => this.logger.error(error, '', 'MmPlanPinSelectionService')
    }));

    this.subscription.add(this.selectionFaultService.selectedItems.pipe(
      debounceTime(500),
      this.takeUntilPlanViewExists(),
      switchMap(items => this.selectionFaultService.selectionMode.pipe(filter(Boolean), map(mode => items))),
      filter(items => items?.length > 0 && this.isPlanSetBySelection === false),
      distinctUntilChanged(),
      filter(p => this.planEditService.planEditMode === PlanEditModeEnum.None),

      untilDestroyed(this),
    ).subscribe({
      next: async (faults) => {
        const filtreredPlans = await firstValueFrom(this.plansFilterService.filterdPlans$.pipe(first()));
        let planWithFault;
        faults.forEach(fault => {
          const plan = filtreredPlans?.find(p => p?.pins?.find(pin => pin?.fault?.identifier === fault?.identifier));
          if (plan && !planWithFault) {
            planWithFault = plan;
            this.isPlanSetBySelection = true;
          }
        });

        if (planWithFault) {
          this.plansManagerService.setSelectedPlan(planWithFault);
        }
      },
      error: (error) => this.logger.error(error, '', 'MmPlanPinSelectionService')
    }));


    this.subscription.add(this.faultsViewService.currentFault
    .pipe(
      filter(Boolean),
      tap(fault => this.planPinService.resetSelectedPins()),
      this.takeUntilPlanViewExists(),
      untilDestroyed(this),
    ).subscribe({
      next: async (currentFault) => {
        let pin = this.planPinService?.pins?.find(p => p.id === currentFault.identifier || p.id === currentFault.uid);

        let planSwitched = false;
        await (this.plansFilterService.filterdPlans$.pipe(
          first(),
          map(plans => plans?.find(plan => plan?.pins?.find(p => p?.fault.id === currentFault.identifier))),
          filter(Boolean),
          tap(plan => {
            if (plan.id !== this.plansManagerService.selectedPlan?.id && !this.appContainerService.isMobileView) {
              this.plansManagerService.setSelectedPlan(plan);
              planSwitched = true;
            }
          })
        )).toPromise();

        if (!pin && planSwitched) {
          pin = await firstValueFrom(
            this.planPinService.pins$.pipe(
              map(pins => pins?.find(p => p.id === currentFault.identifier || p.id === currentFault.uid)),
              filter(Boolean),
              first(),
              timeout(PIN_LOAD_TIMEOUT_MS)
            )
          ).catch(() => null);
        }

        if (pin) {
          this.planPinService.selectPin(pin, false);
          await this.selectFault(pin);

          if (!this.faultsViewService.preventFlyToMarker) {
            setTimeout(() => this.markerSrvice?.flyToMarkers([{id: pin.id}]), 1500);
          }
          this.faultsViewService.preventFlyToMarker = false;
          if (await this.shouldOpenPinPopup()) {
            this.pinPopupService.openPopup(pin.id);
          }
        }
      },
      error: (error) => this.logger.error(error, '', 'MmPlanPinSelectionService')
    }));

    this.subscription.add(this.faultsViewService.currentFault.pipe(
      first(),
      filter(Boolean),
      untilDestroyed(this),
    ).subscribe(async currentFault => {
      const pin = this.findPinForFault(currentFault);
      if (pin && !this.getSelectedPinIds().has(pin.id)) {
        this.planPinService.selectPin(pin, false);
        if (await this.shouldOpenPinPopup()) {
          this.pinPopupService.openPopup(pin.id);
        }
        if (!this.faultsViewService.preventFlyToMarker) {
          this.markerSrvice.flyToMarkers([{id: pin.id}]);
        }
        this.faultsViewService.preventFlyToMarker = false;
      }
    }));

  }

  private async selectFault(pin: Pin) {
    const selectionMode = (await firstValueFrom(this.selectionFaultService.selectionMode));
    const selectedPlan = await firstValueFrom(this.plansManagerService.selectedPlan$);
    if (!!selectionMode) {
      const found = selectedPlan.pins?.find(p => p.fault.identifier === pin.id || p.fault.uid === pin.id)?.fault;
      if (found && (await firstValueFrom(this.selectionFaultService.selectedItems)).findIndex(x => x.data.UniqueId === found.uid)) {
        this.selectionFaultService.selectItem(found);
      }
    }
  }

  private findPinForFault(fault: ViewFault): Pin | undefined {
    const pins = this.planPinService?.pins;
    if (!pins?.length || !fault) {
      return undefined;
    }
    const faultKeys = new Set([fault.identifier, fault.uid, fault.id].filter(k => k != null));
    return pins.find(p => faultKeys.has(p.id));
  }

  private getSelectedPinIds(): Set<unknown> {
    return new Set((this.planPinService?.selectedPins ?? []).map(p => p.id));
  }

  private async shouldOpenPinPopup(): Promise<boolean> {
    const selectedItems = await firstValueFrom(this.selectionFaultService.selectedItems);
    if ((selectedItems?.length ?? 0) > 1) {
      return false;
    }
    return this.getSelectedPinIds().size <= 1;
  }

}
